import { PRIVACY_POLICY_URL } from 'constants/privacyPolicyLink';
import { useStageCandidatePledgeAnticheatingMutation } from 'generated/graphql';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './AnticheatingContract.module.scss';

const AnticheatingContract = ({
  stageCandidateId,
  onPledgeSubmit,
  isIntegrityMonitoringEnabled = false,
  companyName,
}: {
  stageCandidateId: number;
  onPledgeSubmit: () => void;
  // Extends the commitment with the monitoring disclosure + consent checkbox
  // when the project has integrity monitoring enabled.
  isIntegrityMonitoringEnabled?: boolean;
  companyName?: string;
}) => {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const [hasCheckboxError, setHasCheckboxError] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [hasConsentError, setHasConsentError] = useState(false);
  const [updateStatus] = useStageCandidatePledgeAnticheatingMutation({
    variables: {
      id: stageCandidateId,
    },
    onCompleted: () => {
      onPledgeSubmit();
    },
    onError: () => {
      handleMsgType({
        type: TNotification.error,
        title: t('common.genericError'),
      });
    },
  });
  const { handleMsgType } = useNotification();

  const handleCheckboxChange = () => {
    if (hasCheckboxError) {
      setHasCheckboxError(false);
    }

    setIsChecked(prev => !prev);
  };

  const handleConsentChange = () => {
    if (hasConsentError) {
      setHasConsentError(false);
    }

    setIsConsentChecked(prev => !prev);
  };

  const handleSubmit = () => {
    const missingConsent = isIntegrityMonitoringEnabled && !isConsentChecked;
    if (!isChecked || missingConsent) {
      setHasCheckboxError(!isChecked);
      setHasConsentError(missingConsent);
      return;
    }

    updateStatus();
  };

  return (
    <section
      className={styles.container}
      data-testid="stage__assessment-anticheating-contract"
    >
      <h2 className={styles.title}>
        {t(`stage.assessmentPledge.assessmentCommitment`)}
      </h2>
      <div className={styles.pledge}>
        <p className={styles.description}>
          {t(`stage.assessmentPledge.pledge`)}
        </p>

        {isIntegrityMonitoringEnabled && (
          <div className={styles.monitoring}>
            <h3 className={styles.sectionTitle}>
              {t('stage.assessmentPledge.monitoring.whatWeMonitorTitle')}
            </h3>
            <p className={styles.description}>
              {t('stage.assessmentPledge.monitoring.whatWeMonitorIntro')}
            </p>
            <ul className={styles.monitoredList}>
              <li>{t('stage.assessmentPledge.monitoring.monitoredCamera')}</li>
              <li>{t('stage.assessmentPledge.monitoring.monitoredScreen')}</li>
              <li>{t('stage.assessmentPledge.monitoring.monitoredDevice')}</li>
            </ul>
            <p className={styles.description}>
              <Trans
                i18nKey="stage.assessmentPledge.monitoring.whatWeMonitorOutro"
                t={t}
                components={[
                  <a
                    key="privacy-notice"
                    className={styles.privacyLink}
                    href={PRIVACY_POLICY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  />,
                ]}
              />
            </p>
            <h3 className={styles.sectionTitle}>
              {t('stage.assessmentPledge.monitoring.flaggedTitle')}
            </h3>
            <p className={styles.description}>
              {t('stage.assessmentPledge.monitoring.flaggedBody', {
                companyName,
              })}
            </p>
            <p className={styles.bothRequired}>
              {t('stage.assessmentPledge.monitoring.bothRequired')}
            </p>
            <Checkbox
              label={t('stage.assessmentPledge.monitoring.checkboxConsent')}
              checked={isConsentChecked}
              hasError={hasConsentError}
              bottomText={
                hasConsentError ? t(`stage.assessmentPledge.checkboxError`) : ''
              }
              onChange={handleConsentChange}
            />
          </div>
        )}

        <Checkbox
          label={t(`stage.assessmentPledge.checkboxCommitment`)}
          checked={isChecked}
          hasError={hasCheckboxError}
          bottomText={
            hasCheckboxError ? t(`stage.assessmentPledge.checkboxError`) : ''
          }
          onChange={handleCheckboxChange}
        />
      </div>
      <Button className={styles.cta} onClick={handleSubmit}>
        {t(`stage.assessmentPledge.pledgeCta`)}
      </Button>
    </section>
  );
};

export default AnticheatingContract;
